import { connect, useDispatch, useSelector } from "react-redux";
import { State } from "redux/store";
import classNames from "classnames";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import {
  getEntityTypeFilter,
  getIsLabelLoading,
  getSimilarityVertexCluster,
  getTreeFolder,
  isImageSearchEnabled,
  isFolderFilterEnabled,
  getIsImageUploading,
  getIsIssuesLoading,
  getIsFileFilterLoading,
  getIsUserConfigLoading,
  isSavedViewsEnabled,
  getIsUserTagLoading,
  getIsNewFiltersPanel,
} from "redux/SingleDataset/selectors";
import {
  clearFilters,
  fetchUserTags,
  fetchDataset,
  setSimilarityVertex,
  fetchDatasetIssues,
  fetchDatasetContextualLabels,
} from "redux/SingleDataset/actions";
import {
  ENTITY_TYPE_PARAMETER,
  FOLDER_PARAMETER,
  GRANULARITY_PARAMETER,
  LABEL_CLASS_PARAMETER,
  PAGE_PARAMETER,
} from "helpers/constants/miscellaneous";

import useAllQueryParamsFilters from "hooks/useAllQueryParamsFilters";
import { AMP_DATASET_EXPLORATION_PROPERTY__ENTITY_TYPE } from "helpers/constants/amplitudeProperties";
import {
  AMP_DATASET_EXPLORATION_EVENT__FILTER__ALL_CLEARED,
  AMP_DATASET_EXPLORATION_EVENT__TEXTUAL_SEARCH__APPLIED,
  AMP_DATASET_EXPLORATION_EVENT__TEXTUAL_SEARCH__REMOVED,
} from "helpers/constants/amplitudeEvents";
import { amplitudeTrack } from "helpers/utility/amplitude";
import {
  useWindowResize,
  checkAnyFilterApplied,
} from "helpers/utility/utilities";

import TagsFilter from "./TagsFilter";
import FolderFilter from "./FolderFilter";
import SaveViewMenu from "./SaveViewMenu";
import SearchContainer from "./SearchContainer";
import IssueTypeFilter from "./IssueTypeFilter";
import DataPageLabelFilters from "./LabelFilter";
import ClusterDetailsFilter from "./ClusterDetailsFilter";

import { FilterOption, isValidEntityType } from "types";

import styles from "./style.module.scss";
import { FaRegStar } from "react-icons/fa";
import SavedViewsFilter from "./SavedViewsFilter";
import UniquenessFilter from "./UniquenessFilter";
import ChevronIcon from "assets/icons/ChevronIcon";
import AddFilterButton from "./AddFiltersButton";

const DataPageFilters = ({
  isLabelLoading,
  isVertexSelected,
  treeData,
  isTagLoading,
  imageSearchEnabled,
  folderFilterEnabled,
  isImageUploading,
  isIssuesLoading,
  isFileFilterLoading,
  isUserConfigLoading,
  savedViewsEnabled,
}: any) => {
  const queryParamsFilters = useAllQueryParamsFilters();
  const entityTypeFromQueryParam = isValidEntityType(
    queryParamsFilters[ENTITY_TYPE_PARAMETER]
  )
    ? queryParamsFilters[ENTITY_TYPE_PARAMETER]
    : null;
  const entityTypeFilter = useSelector((state: State) =>
    getEntityTypeFilter(state, entityTypeFromQueryParam)
  );

  const isNewFiltersPanel = useSelector((state) => getIsNewFiltersPanel(state));

  const { datasetId, clusterId } = useParams();
  const [isClearFiltersCalled, setIsClearFiltersCalled] = useState(false);
  const [saveViewMenuOpen, setSaveViewMenuOpen] = useState<boolean>(false);
  const [showLeftScrollBtn, setShowLeftScrollBtn] = useState(false);
  const [showRightScrollBtn, setShowRightScrollBtn] = useState(false);
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { width, height } = useWindowResize();
  const scrollableRef = useRef<HTMLDivElement>(null);
  const searchContainerRef = useRef<HTMLDivElement>(null);

  const filterSectionClassnames = classNames(styles.filterSection, {
    [styles.disabledFilterSection]: isImageUploading,
  });

  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  const anyFilterApplied = useMemo(
    () => checkAnyFilterApplied(searchParams),
    [searchParams]
  );

  const labelId = searchParams.get(LABEL_CLASS_PARAMETER);
  const showClusterFilter = (!!clusterId || !!labelId) && !!datasetId;
  const hideEntityFilter = (datasetId && clusterId) || isVertexSelected;

  const showEntityTypeLoader = isUserConfigLoading;
  const showLabelLoader = isLabelLoading || isUserConfigLoading;
  const showIssueLoader = isIssuesLoading || isUserConfigLoading;
  const showTagLoader = isTagLoading || isUserConfigLoading;

  const filtersLoading =
    showEntityTypeLoader || showLabelLoader || showIssueLoader || showTagLoader;

  const handleLeftScroll = () => {
    if (scrollableRef.current) {
      scrollableRef.current.scrollBy({ left: -200, behavior: "smooth" });
    }
  };

  const handleRightScroll = () => {
    if (scrollableRef.current) {
      scrollableRef.current.scrollBy({ left: 200, behavior: "smooth" });
    }
  };

  const removeParameters = (searchParams: any) => {
    let keysToBeDeleted: any = [];
    searchParams.forEach((value: any, key: any) => {
      //ignoring the filters that are not reset
      if (
        [PAGE_PARAMETER, GRANULARITY_PARAMETER, ENTITY_TYPE_PARAMETER].includes(
          key
        )
      ) {
        //do nothing
      } else {
        keysToBeDeleted.push(key);
      }
    });

    if (keysToBeDeleted.length > 0) {
      keysToBeDeleted.forEach((key: any) => searchParams.delete(key));
    }
    return searchParams;
  };

  const handleSaveViewClick = (e: React.MouseEvent<HTMLDivElement>) => {
    setSaveViewMenuOpen(!saveViewMenuOpen);
  };

  const handleClearFilters = async () => {
    setIsClearFiltersCalled(true); //used only for search input for now
    dispatch(clearFilters() as any);

    const selectedEntities = entityTypeFilter
      .filter((entity: FilterOption) => entity.selected)
      .map((entity: FilterOption) => entity.text);
    let value;
    if (selectedEntities.length > 0) {
      value = selectedEntities.join(",");
    } else {
      value = "Images";
    }
    amplitudeTrack(AMP_DATASET_EXPLORATION_EVENT__FILTER__ALL_CLEARED, {
      [AMP_DATASET_EXPLORATION_PROPERTY__ENTITY_TYPE]: value,
    });
    if (!isVertexSelected) {
      searchParams.set(PAGE_PARAMETER, "1");
    }
    const updatedSearchParams = removeParameters(searchParams);
    const newParams = `?${updatedSearchParams.toString()}`;
    let newPath = location.pathname;

    if (newPath.includes("/cluster")) {
      newPath = newPath.split("/cluster")[0];
    }
    dispatch(
      fetchDatasetContextualLabels(datasetId, queryParamsFilters) as any
    );
    dispatch(fetchUserTags(datasetId, queryParamsFilters) as any);
    dispatch(fetchDatasetIssues(datasetId, queryParamsFilters) as any);
    dispatch(fetchDataset(datasetId) as any);
    dispatch(setSimilarityVertex(null));
    navigate(newPath.concat(newParams));
  };

  const logSearchText_Amplitude = (
    entities: FilterOption[],
    context: "added" | "removed"
  ) => {
    const event =
      context === "added"
        ? AMP_DATASET_EXPLORATION_EVENT__TEXTUAL_SEARCH__APPLIED
        : AMP_DATASET_EXPLORATION_EVENT__TEXTUAL_SEARCH__REMOVED;
    const selectedEntities = entities
      .filter((entity) => entity.selected)
      .map((entity) => entity.text);

    let value;
    if (selectedEntities.length > 0) {
      value = selectedEntities.join(",");
    } else {
      value = "Images";
    }

    amplitudeTrack(event, {
      [AMP_DATASET_EXPLORATION_PROPERTY__ENTITY_TYPE]: value,
    });
  };

  const handleFolderSelection = (path: string) => {
    if (path === null) {
      searchParams.delete(FOLDER_PARAMETER);
    } else {
      searchParams.set(FOLDER_PARAMETER, encodeURIComponent(path));
      searchParams.set(PAGE_PARAMETER, "1");
    }
    navigate({ search: `?${searchParams.toString()}` });
  };

  const updateScrollButtons = () => {
    const ROUGH_MARGIN = 5; // extra Margin for scroll positions
    const scrollableContainer = scrollableRef.current;
    if (scrollableContainer) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollableContainer;

      setShowLeftScrollBtn(scrollLeft > ROUGH_MARGIN);
      setShowRightScrollBtn(
        scrollLeft < scrollWidth - clientWidth - ROUGH_MARGIN
      );
    }
  };

  const scrollableFilterStyle = {
    maxWidth: `calc(100% - 120px - ${
      searchContainerRef.current?.offsetWidth || 400
    }px - 81px${
      savedViewsEnabled && anyFilterApplied ? " - 104px" : ""
    } - 16px)`,
  };

  useEffect(() => {
    const scrollableElement = scrollableRef.current;

    if (scrollableElement) {
      scrollableElement?.addEventListener("scroll", updateScrollButtons);
      updateScrollButtons();
    }

    return () => {
      if (scrollableElement) {
        scrollableElement.removeEventListener("scroll", updateScrollButtons);
      }
    };
  }, [width, height, filtersLoading]);

  const renderFilterSection = () => {
    if (isNewFiltersPanel) {
      return (
        <div className={styles.filterSectionLeft}>
          {savedViewsEnabled && <SavedViewsFilter />}
          <div className={styles.filterWrapper}>
            {isUserConfigLoading ? (
              <div className={styles.searchBarLoader}></div>
            ) : (
              <SearchContainer
                containerRef={searchContainerRef}
                datasetId={datasetId!}
                isImageUploading={isImageUploading}
                hideEntityFilter={hideEntityFilter}
                imageSearchEnabled={imageSearchEnabled}
                isUserConfigLoading={isUserConfigLoading}
                isClearFiltersCalled={isClearFiltersCalled}
                showEntityTypeLoader={showEntityTypeLoader}
                setIsClearFiltersCalled={setIsClearFiltersCalled}
                logSearchAddition={() => {
                  logSearchText_Amplitude(entityTypeFilter, "added");
                }}
                logSearchRemoval={() => {
                  logSearchText_Amplitude(entityTypeFilter, "removed");
                }}
              />
            )}
          </div>

          <AddFilterButton />

          {showClusterFilter && <ClusterDetailsFilter />}
        </div>
      );
    }

    //old Filters section
    return (
      <div className={styles.filterSectionLeft}>
        {/* -------------------FILTERS----------------------- */}
        {savedViewsEnabled && <SavedViewsFilter />}
        {isUserConfigLoading ? (
          <div className={styles.searchBarLoader}></div>
        ) : (
          <SearchContainer
            containerRef={searchContainerRef}
            datasetId={datasetId!}
            isImageUploading={isImageUploading}
            hideEntityFilter={hideEntityFilter}
            imageSearchEnabled={imageSearchEnabled}
            isUserConfigLoading={isUserConfigLoading}
            isClearFiltersCalled={isClearFiltersCalled}
            showEntityTypeLoader={showEntityTypeLoader}
            setIsClearFiltersCalled={setIsClearFiltersCalled}
            logSearchAddition={() => {
              logSearchText_Amplitude(entityTypeFilter, "added");
            }}
            logSearchRemoval={() => {
              logSearchText_Amplitude(entityTypeFilter, "removed");
            }}
          />
        )}

        <div
          className={styles.scrollableFiltersContainer}
          style={scrollableFilterStyle}
        >
          <div className={styles.scrollableFilters} ref={scrollableRef}>
            {folderFilterEnabled && (
              <FolderFilter
                datasetId={datasetId!}
                treeData={treeData}
                onSelect={handleFolderSelection}
                entityTypeFilter={entityTypeFilter}
                isFileFilterLoading={isFileFilterLoading || isUserConfigLoading}
              />
            )}

            {showLabelLoader ? (
              <div className={styles.labelFilterLoader}></div>
            ) : (
              <DataPageLabelFilters />
            )}

            {showIssueLoader ? (
              <div className={styles.issueTypeLoader}></div>
            ) : (
              <IssueTypeFilter />
            )}

            {showTagLoader ? (
              <div className={styles.userTagLoader}></div>
            ) : (
              <TagsFilter />
            )}

            <UniquenessFilter />

            {showClusterFilter && <ClusterDetailsFilter />}
          </div>

          {/* absolute positioned scroll buttons*/}
          {showLeftScrollBtn && (
            <div className={styles.leftScrollBtnContainer}>
              <div className={styles.scrollBtn} onClick={handleLeftScroll}>
                <ChevronIcon direction="left" />
              </div>
            </div>
          )}
          {showRightScrollBtn && (
            <div className={styles.rightScrollBtnContainer}>
              <div className={styles.scrollBtn} onClick={handleRightScroll}>
                <ChevronIcon direction="right" />
              </div>
            </div>
          )}
        </div>
        {/* ------------------------------------------------------------------------- */}

        <>
          <div
            className={styles.clearFilterButton}
            onClick={handleClearFilters}
          >
            &nbsp;&nbsp;Clear Filters
          </div>
          {savedViewsEnabled && anyFilterApplied && (
            <div className={styles.saveViewButtonContainer}>
              <div className={styles.saveViewButton}>
                <div className={styles.separator}></div>
                <div
                  onClick={handleSaveViewClick}
                  className={styles.saveViewButtonLabel}
                >
                  <FaRegStar />
                  Save View
                </div>
              </div>
              <div className={styles.saveViewContainer}>
                {saveViewMenuOpen && (
                  <SaveViewMenu
                    onCloseMenu={() => setSaveViewMenuOpen(false)}
                  />
                )}
              </div>
            </div>
          )}
        </>
      </div>
    );
  };

  return <div className={filterSectionClassnames}>{renderFilterSection()}</div>;
};

const mapStatesToProps = (state: State) => {
  return {
    isLabelLoading: getIsLabelLoading(state),
    isIssuesLoading: getIsIssuesLoading(state),
    isFileFilterLoading: getIsFileFilterLoading(state),
    isUserConfigLoading: getIsUserConfigLoading(state),
    isVertexSelected: !!getSimilarityVertexCluster(state),
    treeData: getTreeFolder(state),
    isTagLoading: getIsUserTagLoading(state),
    imageSearchEnabled: isImageSearchEnabled(state),
    folderFilterEnabled: isFolderFilterEnabled(state),
    savedViewsEnabled: isSavedViewsEnabled(state),
    isImageUploading: getIsImageUploading(state),
  };
};

export default connect(mapStatesToProps)(DataPageFilters);
